import { type DirectiveBinding, type ObjectDirective, type Reactive } from "vue";
import type { ElementState, FormBindingValue, FormElement, ValidatorOutput } from "./types";
import { debounce } from "../../utils/helpers";
import { ChqChangeEvent } from "@utils/global-types";

function validate(el: FormElement | null, validatorOutput: Reactive<ValidatorOutput>,
    mode: 'deep-required' | 'shallow-required' | 'deep-all' | 'shallow-all', trackState = false, resetInitial = false) {
    //todo remove this once we confirm it works well
    console.log("validator run");

    if (!el) return { isValid: false }

    const runValidator = function () {
        const validator = validate(el, this, mode, trackState, false);
        Object.assign(this, validator)
    };

    const fields: Record<string, ElementState> = {},
        modes = mode.split('-'),
        elements = modes[1] == 'required'
            ? el.querySelectorAll('[required]')
            : el.querySelectorAll('input, select, textarea')

    if (trackState && mode.includes('shallow')) {
        console.warn('Shallow mode and trackState = true cannot work together')
    }

    for (const el of elements) {

        const input = el as HTMLInputElement
        const inputKey = input.name || input.id

        if (modes[0] === 'shallow' && !input.validity.valid) {
            return { isValid: false, runValidator }
        }
        else if (modes[0] === 'deep') {
            if (!trackState) {
                fields[inputKey] = {
                    isValid: input.validity.valid
                }
                continue
            }

            let currentFields = {} as ElementState;
            let initialValue = getValue(input);

            if (validatorOutput?.fields && validatorOutput.fields[inputKey] && !resetInitial) {
                currentFields = validatorOutput.fields[inputKey];
                initialValue = currentFields.initialValue;
            }

            fields[inputKey] = {
                isValid: input.validity.valid,
                isDirty: currentFields?.isDirty || initialValue != getValue(input),
                initialValue: initialValue
            }
        }
    }

    let values: ElementState[] = []
    if (modes[0] === 'deep') {
        values = Object.values(fields)
    }

    return {
        isValid: modes[0] !== 'deep'
            ? true
            : !values.map(x => x.isValid).some(x => !x),
        isDirty: modes[0] !== 'deep' && trackState
            ? undefined
            : values.map(x => x.isDirty).some(x => x),
        fields,
        setPristine: !trackState ? null : function () {
            const validator = validate(el, this, mode, true, true);
            Object.assign(this, validator)
        },
        runValidator
    }
}

function getValue(input: HTMLInputElement) {
    return (input.type != 'checkbox') ? input.value : input.checked.toString();
}

const formValidation: ObjectDirective<HTMLElement, FormBindingValue> = {

    mounted(el: FormElement, binding: DirectiveBinding<FormBindingValue>) {

        const form = binding.value.form;
        const mode = binding.value?.mode || 'shallow-required';
        const trackState = binding.value?.trackState || false;
        const disableObserver = binding.value?.disableObserver || false;

        if (!form) {
            console.warn('form not set');
            return;
        }

        const execute = function () {
            const validator = validate(el, form, mode, trackState)
            Object.assign(form, validator)
        }

        const execDebounced = debounce(execute, 200)
        let observer: MutationObserver;

        if (!disableObserver) {
            observer = new MutationObserver(execDebounced);
            observer.observe(el, {
                childList: true,
                subtree: true,
                // attributes: true, //attributes caused problems
            });
        }

        el.addEventListener('input', execDebounced)
        document.addEventListener(ChqChangeEvent, execDebounced)
        el.__ListenerCleanup = function () {
            observer?.disconnect?.();
            el.removeEventListener('input', execDebounced);
            document.removeEventListener(ChqChangeEvent, execDebounced)
        }

        //run once on mount
        setTimeout(execute, 100)
    },
    unmounted(el: FormElement) {
        if (el.__ListenerCleanup) {
            el.__ListenerCleanup()
        }
        delete el.__ListenerCleanup
    }
}

export default formValidation